import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { DEFAULT_PAGING, OrganizationService } from 'src/app/@shared';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';
import {
  EventPrintAttributeDefinition,
  EventPrintAttributeSavePayload,
} from '../models/event-print-attribute.model';

export interface EventPrintAttributeListFilters {
  divisionIds: string[];
  attributeTypes: string[];
  eventTypeIds: string[];
}

export interface CopyFromEventAttributeItem {
  id: string;
  name: string;
  type: string;
  divisionNames?: string;
  printPage?: string;
}

export interface CopyFromEventSourceItem {
  id: string;
  name: string;
  divisionNames?: string;
  createdAt?: string;
  eventPrintAttributes: CopyFromEventAttributeItem[];
}

const EMPTY_LIST_FILTERS: EventPrintAttributeListFilters = {
  divisionIds: [],
  attributeTypes: [],
  eventTypeIds: [],
};

@Injectable({
  providedIn: 'root',
})
export class EventPrintAttributeService {
  private loadingBehaviorSubject = new BehaviorSubject<boolean>(false);
  private pageBehaviorSubject = new BehaviorSubject({
    ...DEFAULT_PAGING,
    pageSize: 20,
  });
  private sortBehaviorSubject = new BehaviorSubject({
    active: 'PrintAttributeName',
    direction: 'asc' as 'asc' | 'desc',
  });
  private searchBehaviorSubject = new BehaviorSubject<string>('');
  private reloadBehaviorSubject = new BehaviorSubject<string>('');
  private eventIdBehaviorSubject = new BehaviorSubject<string>('0');
  private listFiltersBehaviorSubject =
    new BehaviorSubject<EventPrintAttributeListFilters>({ ...EMPTY_LIST_FILTERS });
  private targetEventIdBehaviorSubject = new BehaviorSubject<string>('0');
  private ByNotEventIdPageBehaviorSubject = new BehaviorSubject({
    ...DEFAULT_PAGING,
    pageSize: 20,
  });
  private ByNotEventIdSortBehaviorSubject = new BehaviorSubject({
    active: 'Name',
    direction: 'asc' as 'asc' | 'desc',
  });
  private ByNotEventIdSearchBehaviorSubject = new BehaviorSubject<string>('');
  private ByNotEventIdReloadBehaviorSubject = new BehaviorSubject<string>('');
  private sourceEventIdBehaviorSubject = new BehaviorSubject<string>('0');

  private importFromTemplatePageBehaviorSubject = new BehaviorSubject({
    ...DEFAULT_PAGING,
    pageSize: 20,
  });
  private importFromTemplateSortBehaviorSubject = new BehaviorSubject({
    active: 'Name',
    direction: 'asc' as 'asc' | 'desc',
  });
  private importFromTemplateSearchBehaviorSubject = new BehaviorSubject<string>('');
  private importFromTemplateReloadBehaviorSubject = new BehaviorSubject<string>('');

  public isLoading$ = this.loadingBehaviorSubject.asObservable();
  public sort$ = this.sortBehaviorSubject.asObservable();
  public page$ = this.pageBehaviorSubject.asObservable();
  public search$ = this.searchBehaviorSubject.asObservable();
  public reload$ = this.reloadBehaviorSubject.asObservable();
  public listFilters$ = this.listFiltersBehaviorSubject.asObservable();

  public targetEventId$ = this.targetEventIdBehaviorSubject.asObservable();
  public ByNotEventIdPage$ = this.ByNotEventIdPageBehaviorSubject.asObservable();
  public ByNotEventIdSort$ = this.ByNotEventIdSortBehaviorSubject.asObservable();
  public ByNotEventIdSearch$ = this.ByNotEventIdSearchBehaviorSubject.asObservable();
  public ByNotEventIdReload$ = this.ByNotEventIdReloadBehaviorSubject.asObservable();
  public sourceEventId$ = this.sourceEventIdBehaviorSubject.asObservable();

  public importFromTemplatePage$ = this.importFromTemplatePageBehaviorSubject.asObservable();
  public importFromTemplateSort$ = this.importFromTemplateSortBehaviorSubject.asObservable();
  public importFromTemplateSearch$ = this.importFromTemplateSearchBehaviorSubject.asObservable();
  public importFromTemplateReload$ = this.importFromTemplateReloadBehaviorSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private organizationService: OrganizationService
  ) {}

  setEventId(eventId: string): void {
    if (this.eventIdBehaviorSubject.value === eventId) {
      return;
    }
    this.eventIdBehaviorSubject.next(eventId);
    const paging = JSON.parse(JSON.stringify(this.pageBehaviorSubject.getValue()));
    paging.pageIndex = 0;
    this.pageBehaviorSubject.next(paging);
  }

  setListFilters(filters: EventPrintAttributeListFilters): void {
    this.listFiltersBehaviorSubject.next(filters);
    const paging = JSON.parse(JSON.stringify(this.pageBehaviorSubject.getValue()));
    paging.pageIndex = 0;
    this.pageBehaviorSubject.next(paging);
  }

  clearListFilters(): void {
    this.setListFilters({ ...EMPTY_LIST_FILTERS });
  }

  private sortOrderByPath(active: string): string | null {
    const sortMap: Record<string, string> = {
      Name: 'Detail/PrintAttributeName',
      PrintAttributeType: 'Detail/PrintAttributeType',
      EventTypeName: 'EventTypeName',
      Description: 'Detail/Description',
    };
    return sortMap[active] ?? null;
  }

  private buildODataOrderBy(sort: { active: string; direction: string }): string | null {
    const path = this.sortOrderByPath(sort.active);
    if (!path) {
      return null;
    }
    const direction =
      String(sort.direction || 'asc').toLowerCase() === 'desc' ? 'desc' : 'asc';
    return `${path} ${direction}`;
  }

  public params$ = combineLatest([
    this.pageBehaviorSubject.pipe(debounceTime(300)),
    this.sortBehaviorSubject,
    this.searchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject,
    this.eventIdBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
    map(([page, sort, search, reload, eventId]) => {
      let params: HttpParams = new HttpParams({
        fromObject: {
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $count: true,
          $expand: 'Detail',
        },
      });

      const orderBy = this.buildODataOrderBy(sort);
      if (orderBy) {
        if(orderBy.includes('Detail/PrintAttributeType')) {
          params = params.set('OrderBy', orderBy);
        } else {
          params = params.set('$orderby', orderBy);
        }
      }

      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      if (eventId && eventId !== '0') {
        params = params.append('eventId', eventId);
      }
      
      return params;
    })
  );

  sort(sort: any): void {
    this.sortBehaviorSubject.next(sort);
    const paging = JSON.parse(JSON.stringify(this.pageBehaviorSubject.getValue()));
    paging.pageIndex = 0;
    this.pageBehaviorSubject.next(paging);
  }

  reload(): void {
    this.reloadBehaviorSubject.next(uuidv4());
  }

  page(page: any): void {
    this.pageBehaviorSubject.next(page);
  }

  search(search: string): void {
    this.searchBehaviorSubject.next(search);
    const paging = JSON.parse(JSON.stringify(DEFAULT_PAGING));
    paging.pageSize = 20;
    this.pageBehaviorSubject.next(paging);
  }

  private eventPrintAttributeResponse$ = this.params$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)),
    switchMap((params) => {
      const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventPrintAttributeDomains/GetAll`;
      return this.httpClient.get<any>(url, { params });
    }),
    tap(() => this.loadingBehaviorSubject.next(false)),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  public eventPrintAttributes$: Observable<EventPrintAttributeDefinition[]> =
    this.eventPrintAttributeResponse$.pipe(
      map((res: any) => {
        if (Array.isArray(res?.value)) {
          return res.value;
        }
        if (Array.isArray(res)) {
          return res;
        }
        return [];
      })
    );

  public totalRecords$: Observable<number> = this.eventPrintAttributeResponse$.pipe(
    map((res: any) => {
      if (typeof res?.['@odata.count'] === 'number') {
        return res['@odata.count'];
      }
      if (Array.isArray(res?.value)) {
        return res.value.length;
      }
      if (Array.isArray(res)) {
        return res.length;
      }
      return 0;
    })
  );

  createEventPrintAttribute(payload: EventPrintAttributeSavePayload) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventPrintAttributeDomains`;
    return this.httpClient.post(url, payload);
  }

  getByEventId(eventId: string): Observable<EventPrintAttributeDefinition[]> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventPrintAttributeDomains/GetByEventId`;
    return this.httpClient.get<EventPrintAttributeDefinition[]>(url, {
      params: new HttpParams().set('eventId', eventId),
    });
  }

  getEventPrintAttributeDefinition(id: string, eventId: string): Observable<EventPrintAttributeDefinition> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventPrintAttributeDomains/${id}`;
    return this.httpClient.get<EventPrintAttributeDefinition>(url, {
      params: new HttpParams().set('$expand', 'Detail').set('eventId', eventId),
    });
  }

  updateEventPrintAttribute(id: string, payload: EventPrintAttributeSavePayload) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventPrintAttributeDomains/${id}`;
    return this.httpClient.put(url, payload);
  }

  deleteEventPrintAttribute(id: string, eventId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventPrintAttributeDomains/${id}`;
    return this.httpClient.delete(url, { params: new HttpParams().set('eventId', eventId) });
  }

  deleteEventPrintAttributes(eventId: string, eventPrintAttributeIds: string[]) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventPrintAttributeDomains/BulkDelete`;
    let params = new HttpParams().set('eventId', eventId);
    return this.httpClient.post(url, eventPrintAttributeIds, {params, responseType: 'text' });
  }

  cloneEventPrintAttributes(eventId: string, sourceEventPrintAttributeIds: string[]) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventPrintAttributeDomains/Clone`;
    let params = new HttpParams().set('eventId', eventId);
    return this.httpClient.post(url, sourceEventPrintAttributeIds, { params });
  }

  assignPrintPage(printPageId: string, attributeIds: string[], eventId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventPrintAttributeDomains/BulkAssignPrintPage`;
    let params = new HttpParams().set('printPageId', printPageId).set('eventId', eventId);
    return this.httpClient.post(url, attributeIds, { params, responseType: 'text' });
  }

  importPrintAttributes(eventId: string, printAttributeIds: string[]) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventPrintAttributeDomains/CopyAttributes`;
    let params = new HttpParams().set('eventId', eventId);
    return this.httpClient.post(url, printAttributeIds, { params, responseType: 'text' });
  }

  getImportablePrintAttributes(search?: string): Observable<Array<{ id: string; name: string; attrType: string }>> {
    let params = new HttpParams()
      .set('$expand', 'Detail')
      .set('eventId', this.eventIdBehaviorSubject.value)
      .set('$orderby', 'Detail/PrintAttributeName asc');

    if (search?.trim()) {
      params = params.set('$search', `"${search.trim()}"`);
    }

    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PrintAttributeDomains/ByNotEventId`;
    return this.httpClient.get(url, { params }).pipe(
      map((res: any) =>
        (res?.value ?? [])
          .map((row: any) => ({
            id: row?.DomainId || row?.Detail?.Id || '',
            name: row?.Detail?.PrintAttributeName || 'Untitled Attribute',
            attrType: row?.Detail?.PrintAttributeType || '',
          }))
          .filter((x: { id: string }) => !!x.id)
      )
    );
  }

  set sourceEventId(sourceEventId: string) {
    this.sourceEventIdBehaviorSubject.next(sourceEventId);
  }
  get sourceEventId(): string {
    return this.sourceEventIdBehaviorSubject.value;
  }
  set targetEventId(targetEventId: string) {
    this.targetEventIdBehaviorSubject.next(targetEventId);
  }

  get targetEventId(): string {
    return this.targetEventIdBehaviorSubject.value;
  }

  set ByNotEventIdPage(page: any) {
    this.ByNotEventIdPageBehaviorSubject.next(page);
  }
  get ByNotEventIdPage(): any {
    return this.ByNotEventIdPageBehaviorSubject.value;
  }
  set ByNotEventIdSort(sort: any) {
    this.ByNotEventIdSortBehaviorSubject.next(sort);
  }
  get ByNotEventIdSort(): any {
    return this.ByNotEventIdSortBehaviorSubject.value;
  }
  set ByNotEventIdSearch(search: string) {
    this.ByNotEventIdSearchBehaviorSubject.next(search);
  }
  get ByNotEventIdSearch(): string {
    return this.ByNotEventIdSearchBehaviorSubject.value;
  }

  byNotEventIdReload(): void {
    this.ByNotEventIdReloadBehaviorSubject.next(uuidv4());
  }

  /** Clears copy/import modal state. Call when the add-attribute dialog closes. */
  resetCopyImportModalState(): void {
    this.sourceEventIdBehaviorSubject.next('0');
    this.targetEventIdBehaviorSubject.next('0');
    this.ByNotEventIdSearchBehaviorSubject.next('');
    this.ByNotEventIdPageBehaviorSubject.next({ ...DEFAULT_PAGING, pageSize: 20 });
    this.ByNotEventIdSortBehaviorSubject.next({
      active: 'Name',
      direction: 'asc',
    });

    this.importFromTemplateSearchBehaviorSubject.next('');
    this.importFromTemplatePageBehaviorSubject.next({ ...DEFAULT_PAGING, pageSize: 20 });
    this.importFromTemplateSortBehaviorSubject.next({
      active: 'Name',
      direction: 'asc',
    });

    this.loadingBehaviorSubject.next(false);
  }

  ByNotEventIdParams$ = combineLatest([
    this.sourceEventIdBehaviorSubject.pipe(debounceTime(300)),
    this.targetEventIdBehaviorSubject.pipe(debounceTime(300)),
    this.ByNotEventIdPageBehaviorSubject.pipe(debounceTime(300)),
    this.ByNotEventIdSearchBehaviorSubject.pipe(debounceTime(300)),
    this.ByNotEventIdReloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
    map(([sourceEventId, targetEventId, page, search]) => {
      let params = new HttpParams({
        fromObject: {
          $expand: 'Detail',
          $count: true,
        },
      });
      if (sourceEventId) {
        params = params.set('sourceEventId', sourceEventId);
      }
      if (targetEventId) {
        params = params.set('targetEventId', targetEventId);
      }
      if (page) {
        params = params.set('$skip', String(page.pageIndex * page.pageSize));
        params = params.set('$top', String(page.pageSize));
      }
      if (search) {
        params = params.set('$search', `"${search}"`);
      }
      params = params.set('eventId', this.eventIdBehaviorSubject.value);
      return params;
    }),
  );

  ByNotEventIdResponse$ = this.ByNotEventIdParams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)),
    switchMap((params) => {
      const sourceEventId = params.get('sourceEventId') ?? '';
      const targetEventId = params.get('targetEventId') ?? '';
      const hasBothEventIds =
        !!sourceEventId &&
        !!targetEventId &&
        sourceEventId !== '0' &&
        targetEventId !== '0';
      if (!hasBothEventIds) {
        return of({ value: [] });
      }
      const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventPrintAttributeDomains/ByNotEventId`;
      return this.httpClient.get<any>(url, { params });
    }),
    tap(() => this.loadingBehaviorSubject.next(false)),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  ByNotEventIdAttributes$: Observable<EventPrintAttributeDefinition[]> = this.ByNotEventIdResponse$.pipe(
    map((res: any) => {
      if (Array.isArray(res?.value)) {
        return res.value;
      }
      return [];
    }),
  );

  ByNotEventIdTotalRecords$: Observable<number> = this.ByNotEventIdResponse$.pipe(
    map((res: any) => (typeof res?.['@odata.count'] === 'number' ? res['@odata.count'] : 0)),
  );

  byNotEventIdPage(page: any): void {
    this.ByNotEventIdPageBehaviorSubject.next(page);
  }

  copyFromEvent(
    sourceEventId: string,
    targetEventId: string,
    sourceEventPrintAttributeIds?: string[],
  ) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventPrintAttributeDomains/CopyAttributesFromEvent`;
    let params = new HttpParams()
      .set('sourceEventId', sourceEventId)
      .set('targetEventId', targetEventId);

    return this.httpClient.post(url, sourceEventPrintAttributeIds, { params, responseType: 'text' });
  }

  downloadEventPrintAttributesCSV(eventId: string): void {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventPrintAttribute/Export?eventId=${eventId}`;
    window.open(url, '_blank');
  }

  private importFromTemplateParams$ = combineLatest([
    this.eventIdBehaviorSubject.pipe(debounceTime(300)),
    this.importFromTemplatePageBehaviorSubject.pipe(debounceTime(300)),
    this.importFromTemplateSortBehaviorSubject.pipe(debounceTime(300)),
    this.importFromTemplateSearchBehaviorSubject.pipe(debounceTime(300)),
    this.importFromTemplateReloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
    map(([eventId, page, sort, search]) => {
      let params = new HttpParams({
        fromObject: {
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $count: true,
          $expand: 'Detail',
        },
      });
      const orderBy = this.buildODataOrderBy(sort);
      if (orderBy) {
        params = params.set('$orderby', orderBy);
      }
      if (search) {
        params = params.set('$search', `"${search}"`);
      }
      if (eventId) {
        params = params.set('eventId', eventId);
      }
      return params;
    }),
  );

  importFromTemplateResponse$ = this.importFromTemplateParams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)),
    switchMap((params) => {
      const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PrintAttributeDomains/ByNotEventId`;
      return this.httpClient.get<any>(url, { params });
    })
  ).pipe(
    tap(() => this.loadingBehaviorSubject.next(false)),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  importFromTemplateAttributes$: Observable<EventPrintAttributeDefinition[]> = this.importFromTemplateResponse$.pipe(
    map((res: any) => {
      if (Array.isArray(res?.value)) {
        return res.value;
      }
      return [];
    }),
  );

  importFromTemplateTotalRecords$: Observable<number> = this.importFromTemplateResponse$.pipe(
    map((res: any) => (typeof res?.['@odata.count'] === 'number' ? res['@odata.count'] : 0)),
  );

  importFromTemplatePage(page: any): void {
    this.importFromTemplatePageBehaviorSubject.next(page);
  }

  importFromTemplateSort(sort: any): void {
    this.importFromTemplateSortBehaviorSubject.next(sort);
  }
  importFromTemplateSearch(search: string): void {
    this.importFromTemplateSearchBehaviorSubject.next(search);
  }
  importFromTemplateReload(): void {
    this.importFromTemplateReloadBehaviorSubject.next(uuidv4());
  }

  copyAllAttributesFromEvent(sourceEventId: string, targetEventId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventPrintAttributeDomains/CopyAllAttributesFromEvent`;
    let params = new HttpParams()
      .set('sourceEventId', sourceEventId)
      .set('targetEventId', targetEventId);
    return this.httpClient.post(url, {}, { params, responseType: 'text' });
  }

  clearFilters(): void {
    this.pageBehaviorSubject.next({ ...DEFAULT_PAGING, pageIndex: 0, pageSize: 20 });
    this.sortBehaviorSubject.next({ active: 'PrintAttributeName', direction: 'asc' });
    this.searchBehaviorSubject.next('');
  }
}
